// Bandit manchot : avant chaque niveau, trois rouleaux tirent le personnage du joueur, son
// adversaire et l'action (core/slot.js). Le levier lance les rouleaux, qui s'arrêtent l'un après
// l'autre ; la règle du scénario de l'action tirée s'affiche (« Tu joues 🐨, ramasse toutes les 🪶,
// puis chatouille 🐮 ! », voir core/scenarios/) avec le bouton Jouer.
// Tirage bonus (core/slot.js#drawSpin) : le troisième rouleau tombe sur un personnage. Le jeu applique le bonus (onBonus),
// l'écran l'annonce (« Une paire ! Gain doublé »…) et le bouton Relancer remplace Jouer.
import { $, show, hide, setTextWithEmoji, haloClass } from './dom.js';
import { drawCast, drawSpin, reelStrip, castParams, spinTickTimes } from '../core/slot.js';

// Durée de rotation de chaque rouleau (ms) et nombre de symboles qui défilent
const REELS = [
  { durationMs: 1300, length: 16 },
  { durationMs: 1800, length: 22 },
  { durationMs: 2300, length: 28 },
];
// Les rouleaux attendent que l'audio soit prêt pour partir avec le son, sans jamais attendre plus longtemps (ms)
const AUDIO_WAIT_MS = 400;
const IDLE_SYMBOL = '?'; // texte, pas l'emoji ❓ (rouge) : blanc-gris sur les rouleaux au repos (.slot-symbol)

/**
 * @param {object} options
 * @param {(key: string, params?: object) => string} options.t
 * @param {string[]} options.characters  personnages des deux premiers rouleaux (data/cast.js)
 * @param {{id:string, emoji:string}[]} options.actions  actions du troisième
 * @param {{id:string, emoji:string}|null} [options.forcedAction]  action imposée au troisième rouleau (tests en développement)
 * @param {'none'|'pair'|'triple'|null} [options.forcedBonus]  tirage bonus imposé au premier tirage de chaque ouverture (tests
 *   en développement) ; les relances suivantes sont tirées au hasard
 * @param {(bonus: 'none'|'pair'|'triple') => object} [options.onBonus]  appliquée quand un tirage bonus s'arrête ; rend les
 *   paramètres de son annonce (slot.bonus.<bonus> : {multiplier}, {points}), et `until`, une promesse : le bouton Relancer
 *   n'apparaît qu'une fois résolue (la séquence du score d'un triple)
 * @param {(result: {reels: string[], cast: object|null, bonus: string|null}) => void} [options.onReveal]  appelée quand les
 *   rouleaux sont arrêtés et que le résultat s'affiche (avant le bouton Jouer ou Relancer)
 * @param {{unlock?: () => Promise<unknown>, lever?: () => void, ticks?: (timesMs: number[], durationMs: number) => void,
 *   stop?: (reel: number) => void, reveal?: () => void, start?: () => void}} [options.sound]  les sons du bandit
 *   (services/audio.js) : unlock débloque l'audio dans le clic sur « Nouvelle partie » (exigence d'iOS), puis le levier,
 *   les « tic » de chaque rouleau (instants en ms) et la durée de la roue, l'arrêt de chacun, la sonnerie de la règle, le motif de « Jouer »
 * @param {() => number} [options.random]
 * @param {(cb: () => void, ms: number) => unknown} [options.wait]  injectable pour les tests
 */
export function createSlotMachine({
  t, characters, actions, forcedAction = null, forcedBonus = null, onBonus = () => ({}), onReveal = () => {}, sound = {},
  random = Math.random,
  wait = (cb, ms) => setTimeout(cb, ms),
}) {
  const root = $('slotMachine');
  const strips = [0, 1, 2].map((i) => $(`slotReel${i}`));
  const sentence = $('slotSentence');
  const sentenceBox = sentence.parentElement; // l'écran de la règle
  const btnSpin = $('btnSlotSpin');
  const btnPlay = $('btnSlotPlay');
  const btnRespin = $('btnSlotRespin');
  let result = null; // le tirage en cours : { reels, cast, bonus } (core/slot.js#drawSpin), null au repos
  let revealed = false; // rouleaux arrêtés : l'écran montre la règle ou l'annonce
  let bonusParams = {}; // paramètres de l'annonce du tirage bonus, rendus par onBonus
  let firstSpin = true; // premier tirage depuis l'ouverture : celui où s'applique forcedBonus
  let onPlay = null;

  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  function fill(strip, symbols) {
    strip.replaceChildren(...symbols.map((symbol) => {
      const cell = document.createElement('div');
      cell.className = `slot-symbol ${haloClass(symbol)}`.trim();
      cell.textContent = symbol;
      return cell;
    }));
  }

  /** Remet un rouleau en haut de sa bande, sans animation. */
  function rewind(strip) {
    strip.style.transition = 'none';
    strip.style.transform = 'translateY(0)';
  }

  const ruleOf = (c) => t(`scenario.${c.action.id}.rule`, castParams(t, c));

  // Ce que dit l'écran une fois les rouleaux arrêtés : la règle du scénario, ou l'annonce du tirage bonus
  const screenText = () => {
    if (result.cast) return ruleOf(result.cast);
    return t(`slot.bonus.${result.bonus}`, bonusParams);
  };

  function renderSentence() {
    // Avant le tirage et pendant qu'il tourne, un écran éteint à la place déjà réservée pour la règle ; il s'allume avec elle
    sentenceBox.classList.toggle('is-off', !revealed);
    sentenceBox.classList.toggle('is-bonus', revealed && !result.cast);
    setTextWithEmoji(sentence, revealed ? screenText() : '');
  }

  /** Langue changée : la règle suit. */
  function refresh() {
    if (!root.hidden) renderSentence();
  }

  /** Lance les rouleaux (et, si l'audio est prêt, le son) ; appelée une seule fois par tirage. */
  function startReels(finals, pools, instant) {
    REELS.forEach(({ durationMs, length }, i) => {
      const strip = strips[i];
      const symbols = instant ? [finals[i]] : reelStrip(pools[i], finals[i], length, random);
      fill(strip, symbols);
      rewind(strip);
      if (instant) return;
      void strip.offsetHeight; // applique la position de départ avant de lancer la transition
      // Légère surcourse à l'arrêt, comme un vrai rouleau qui se cale
      strip.style.transition = `transform ${durationMs}ms cubic-bezier(0.15, 0.85, 0.3, 1.04)`;
      strip.style.transform = `translateY(calc(var(--slot-cell) * ${-(symbols.length - 1)}))`;
      wait(() => { navigator.vibrate?.(25); sound.stop?.(i); }, durationMs);
    });

    wait(() => {
      // Rouleaux arrêtés : plus d'animation, sinon un changement de taille d'écran (taille des cases
      // recalculée) les ferait de nouveau défiler
      strips.forEach((strip) => { strip.style.transition = 'none'; });
      revealed = true;
      onReveal(result);
      if (!result.cast) bonusParams = onBonus(result.bonus) ?? {};
      renderSentence();
      sound.reveal?.();
      const next = result.cast ? btnPlay : btnRespin;
      const showNext = () => { show(next); next.focus(); };
      const until = result.cast ? null : bonusParams.until;
      if (!until) {
        showNext();
        return;
      }
      const spun = result;
      Promise.resolve(until).catch(() => {}).then(() => {
        if (result === spun && !root.hidden) showNext(); // sauf si le bandit a été rouvert ou fermé entre-temps
      });
    }, instant ? 0 : REELS.at(-1).durationMs + 150);
  }

  function spin() {
    if (result) return;
    hide(btnSpin);
    hide(btnRespin);
    if (forcedAction) {
      const cast = { ...drawCast(characters, actions, random), action: forcedAction };
      result = { reels: [cast.player, cast.rival, forcedAction.emoji], cast, bonus: null };
    } else {
      result = drawSpin(characters, actions, random, { bonus: firstSpin ? forcedBonus : null });
    }
    firstSpin = false;
    revealed = false;
    renderSentence();
    const finals = result.reels;
    // Le troisième rouleau porte les actions et les personnages : il peut s'arrêter sur un personnage (tirage bonus)
    const pools = [characters, characters, [...actions.map((a) => a.emoji), ...characters]];
    const instant = reducedMotion();

    // Levier et « tic » : joués une fois, dès que les rouleaux tournent ET que l'audio est prêt. Si l'audio arrive après
    // le départ des rouleaux (déblocage lent), les « tic » sont décalés du temps écoulé pour rester calés sur l'image.
    let reelsStartedAt = null;
    let unlocked = !sound.unlock;
    let soundPlayed = false;
    const playSpinSound = () => {
      if (instant || soundPlayed || !unlocked || reelsStartedAt === null) return;
      soundPlayed = true;
      const elapsed = performance.now() - reelsStartedAt;
      const duration = REELS.at(-1).durationMs;
      sound.lever?.();
      sound.ticks?.(spinTickTimes(duration).map((ms) => ms - elapsed), duration - elapsed);
    };
    const go = () => {
      if (reelsStartedAt !== null) return;
      reelsStartedAt = performance.now();
      startReels(finals, pools, instant);
      playSpinSound();
    };

    if (sound.unlock) {
      // Les rouleaux partent quand l'audio est prêt (quelques dizaines de ms au premier tirage, rien ensuite), au plus
      // tard après AUDIO_WAIT_MS : sans cela, le début du son tomberait avant que l'audio existe.
      Promise.resolve(sound.unlock()).catch(() => {}).then(() => { unlocked = true; go(); playSpinSound(); });
      wait(go, AUDIO_WAIT_MS);
    } else {
      go();
    }
  }

  btnSpin.addEventListener('click', spin);
  // Après un tirage bonus : on relance, rouleaux et écran repartent comme au levier
  btnRespin.addEventListener('click', () => {
    result = null;
    spin();
  });
  // Appel synchrone dans le clic : le jeu peut y débloquer l'audio (exigence d'iOS)
  btnPlay.addEventListener('click', () => {
    if (!result?.cast) return; // pas de distribution tirée (bouton caché, tirage bonus) : rien à jouer
    sound.start?.();
    hide(root);
    const done = onPlay;
    onPlay = null;
    done?.(result.cast);
  });

  /**
   * Affiche le bandit, rouleaux au repos, levier prêt.
   * @param {(cast: {player:string, rival:string, action:{id:string, emoji:string}}) => void} play
   *   appelée avec la distribution tirée quand le bouton Jouer est touché
   */
  function open(play) {
    result = null;
    revealed = false;
    firstSpin = true;
    strips.forEach((strip) => { fill(strip, [IDLE_SYMBOL]); rewind(strip); });
    hide(btnPlay);
    hide(btnRespin);
    show(btnSpin);
    show(root);
    renderSentence();
    // Focus différé : la touche Entrée qui a validé l'écran précédent ne doit pas tirer le levier
    wait(() => btnSpin.focus(), 0);
    onPlay = play;
  }

  return { open, refresh, isOpen: () => !root.hidden };
}
